import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Component from 'flarum/common/Component';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Alert from 'flarum/common/components/Alert';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';

/**
 * `lockAction` is added to `DiscussionControls` at runtime by the flarum/lock
 * extension and is therefore not part of core's typings.
 */

export default class BlogPostController extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loadedPost", false);
    _defineProperty(this, "loading", false);
  }
  manageArticleButtons() {
    var _article$canLock;
    const article = this.attrs.article;
    const items = new ItemList();

    // Working for GlowingBlue version
    const languageExtension = flarum.extensions['fof-discussion-language'];
    const LanguageDiscussionModal = languageExtension && typeof languageExtension.components !== 'undefined' ? languageExtension.components.LanguageDiscussionModal : null;

    // Rename article
    if (article.canRename()) {
      items.add('rename', m(Button, {
        className: "Button",
        onclick: () => app.modal.show(() => import(/* webpackChunkName: 'forum/components/Modals/RenameArticleModal', webpackMode: 'lazy-once' */ './Modals/RenameArticleModal'), {
          article
        }),
        icon: "fas fa-pencil-alt"
      }, app.translator.trans('fof-blog.forum.tools.rename_article')), 100);
    }
    const articlePost = article.firstPost();

    // Edit article
    items.add('edit', m(Button, {
      className: "Button",
      disabled: !articlePost || !articlePost.canEdit(),
      onclick: () => {
        app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/EditPostComposer'), {
          post: articlePost
        }).then(() => {
          app.composer.show();
        });
      },
      icon: "fas fa-edit"
    }, app.translator.trans('fof-blog.forum.tools.edit_article')), 90);

    // Article settings
    items.add('articleSettings', m(Button, {
      className: "Button",
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'forum/components/Modals/BlogPostSettingsModal', webpackMode: 'lazy-once' */ './Modals/BlogPostSettingsModal'), {
        article
      }),
      icon: "fas fa-cogs"
    }, app.translator.trans('fof-blog.forum.tools.article_settings')), 80);

    // Update categories
    if (article.canTag()) {
      items.add('tag', m(Button, {
        className: "Button",
        onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('ext:flarum/tags/forum/components/TagDiscussionModal'), {
          discussion: article
        }),
        icon: "fas fa-tag"
      }, app.translator.trans('fof-blog.forum.tools.update_category')), 70);
    }
    const blogMeta = article.blogMeta();

    // Update article SEO. fof/seo is an optional integration, so its modal is
    // lazily resolved from the extension registry.
    if (blogMeta && 'fof-seo' in flarum.extensions && app.forum.attribute('canConfigureSeo')) {
      items.add('seo', m(Button, {
        className: "Button",
        onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('ext:fof/seo/common/Components/MetaSeoModal'), {
          objectType: 'blogs',
          objectId: blogMeta.id()
        }),
        icon: "fas fa-search"
      }, app.translator.trans('fof-seo.forum.controls.configure_seo')), 70);
    }

    // Approve article
    if (blogMeta && blogMeta.isPendingReview()) {
      items.add('separator1', m("li", {
        className: "Dropdown-separator"
      }), 65);
      items.add('approve', m(Button, {
        className: "Button",
        disabled: !app.forum.attribute('canApproveBlogPosts'),
        onclick: () => {
          blogMeta.save({
            isPendingReview: false
          }).then(() => {
            app.alerts.show(Alert, {
              type: 'success'
            }, app.translator.trans('fof-blog.forum.review_article.approve_article_approved'));
          }, () => {
            // `BlogPostController` extends `Component`, which (unlike
            // `Modal`) has no `handleErrors` method, so the previous
            // `this.handleErrors(response)` call would have thrown. We
            // simply reset the loading flag here; Flarum's global request
            // error handler still surfaces the failure to the user.
            this.loading = false;
          });
        },
        icon: "fas fa-thumbs-up"
      }, app.translator.trans('fof-blog.forum.review_article.approve_article')), 60);
    }

    // Language
    if (article.canChangeLanguage && article.canChangeLanguage() && LanguageDiscussionModal) {
      items.add('lang', m(Button, {
        icon: "fas fa-globe",
        onclick: () => app.modal.show(LanguageDiscussionModal, {
          discussion: article
        })
      }, app.translator.trans('fof-discussion-language.forum.discussion_controls.change_language_button')), 50);
    }
    items.add('separator2', m("li", {
      className: "Dropdown-separator"
    }), 40);

    // Lock article (flarum/lock is an optional dependency)
    if ((_article$canLock = article.canLock) != null && _article$canLock.call(article)) {
      var _article$isLocked, _article$isLocked2;
      items.add('lock', m(Button, {
        className: "Button",
        onclick: DiscussionControls.lockAction.bind(article),
        icon: "fas ".concat((_article$isLocked = article.isLocked) != null && _article$isLocked.call(article) ? 'fa-comments' : 'fa-comment-slash')
      }, (_article$isLocked2 = article.isLocked) != null && _article$isLocked2.call(article) ? app.translator.trans('fof-blog.forum.tools.enable_comments') : app.translator.trans('fof-blog.forum.tools.disable_comments')), 30);
    }

    // Hide/show/delete
    if (article.canHide()) {
      // Article is hidden
      if (article.isHidden()) {
        // Recover article
        items.add('recover', m(Button, {
          className: "Button",
          onclick: DiscussionControls.restoreAction.bind(article),
          icon: "fas fa-eye"
        }, app.translator.trans('fof-blog.forum.tools.recover_article')), 20);

        // Delete article
        if (article.canDelete()) {
          items.add('delete', m(Button, {
            className: "Button",
            onclick: () => {
              // Confirm deletion
              if (confirm(extractText(app.translator.trans('core.forum.discussion_controls.delete_confirmation')))) {
                // Redirect if the current page is an blog article
                if (app.history.getCurrent().name === 'blogArticle') {
                  if (app.previous) {
                    app.history.back();
                  } else {
                    m.route.set(app.route('blog'));
                  }
                }
                article.delete().then(() => {
                  m.redraw();
                });
              }
            },
            icon: "far fa-trash-alt"
          }, app.translator.trans('fof-blog.forum.tools.delete_forever')), 10);
        }
      } else {
        // Hide article
        items.add('hide', m(Button, {
          className: "Button",
          onclick: DiscussionControls.hideAction.bind(article),
          icon: "fas fa-eye-slash"
        }, app.translator.trans('fof-blog.forum.tools.hide_article')), 0);
      }
    }
    return items;
  }
  view(vnode) {
    const article = this.attrs.article;
    const articlePost = article.firstPost();
    return m("div", {
      className: 'FoFBlog-Article-Content-Edit-Button'
    }, m("div", {
      className: 'FoFBlog-Article-Content-Edit-Dropdown'
    }, m(Dropdown, {
      icon: "fas fa-cog",
      label: "Manage",
      buttonClassName: "Button",
      menuClassName: "Dropdown-menu--right",
      onshow: () => {
        // Get post data to make sure they can edit the post
        if (articlePost && !articlePost.canEdit() && !this.loadedPost) {
          this.loadedPost = true;
          m.redraw();
        }
      }
    }, this.manageArticleButtons().toArray())));
  }
}
flarum.reg.add('fof-blog', 'forum/components/BlogPostController', BlogPostController);flarum.reg.addChunkModule('786', '602', 'fof-blog', 'forum/components/Modals/RenameArticleModal');
flarum.reg.addChunkModule('851', '720', 'fof-blog', 'forum/components/Modals/BlogPostSettingsModal');